import React from 'react';
import { Link } from 'react-router-dom';
import { Check, ArrowRight } from 'lucide-react';
import { IMAGES, PHILOSOPHY, VENUE_FEATURES, COMMITMENTS } from '../mock/data';
import useScrollReveal from '../hooks/useScrollReveal';
import Seo from '../components/Seo';

function PageHero({ overline, title }) {
  return (
    <section className="relative h-[60vh] min-h-[420px] flex items-center justify-center overflow-hidden">
      <img src={IMAGES.diningInterior} alt={title} className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-ink/65" />
      <div className="relative z-10 text-center px-6">
        <span className="text-cream/80 text-xs tracking-luxe uppercase">{overline}</span>
        <h1 className="font-display text-5xl md:text-6xl text-cream mt-5">{title}</h1>
      </div>
    </section>
  );
}

export default function About() {
  useScrollReveal();
  return (
    <div>
      <Seo
        title="About Us — Where East Meets West"
        path="/about"
        description="Vivamore Dining brings familiar, heartwarming Western-Asian flavours to a refined rooftop at Level 6, NU Sentral Mall, KL. Built around Convenient, Connect, Celebrate."
        keywords="about Vivamore Dining, rooftop venue KL, NU Sentral restaurant, Western Asian comfort food, event space Kuala Lumpur"
      />
      <PageHero overline="Our Story" title="About Vivamore" />

      <section className="py-24 md:py-32 bg-cream">
        <div className="max-w-4xl mx-auto px-6 text-center reveal">
          <span className="text-xs tracking-luxe uppercase text-gold">Our Story</span>
          <h2 className="font-display text-4xl md:text-5xl text-ink mt-4 leading-tight">Contemporary Experiences Designed Around Connection</h2>
          <div className="gold-line mx-auto my-8" />
          <p className="text-ink/70 font-light leading-relaxed mb-5">
            Vivamore was created as a modern social and dining destination where convenience, meaningful connection, and celebration come together naturally. Strategically located in the heart of Kuala Lumpur at NU Mall, Vivamore provides a premium contemporary setting for intimate events, elegant dining experiences, and memorable gatherings.
          </p>
          <p className="text-ink/70 font-light leading-relaxed">
            Every detail — from ambiance and hospitality to cuisine and event experiences — is thoughtfully curated to create moments that feel personal, comfortable, and unforgettable.
          </p>
        </div>
      </section>

      {/* Philosophy */}
      <section className="py-24 md:py-32 bg-ink">
        <div className="max-w-7xl mx-auto px-6">
          <div className="reveal text-center flex flex-col items-center mb-16">
            <span className="text-xs tracking-luxe uppercase text-gold-light">Our Philosophy</span>
            <h2 className="font-display text-4xl md:text-5xl text-cream mt-4">What Defines Us</h2>
            <div className="gold-line mt-6" />
          </div>
          <div className="grid md:grid-cols-3 gap-10">
            {PHILOSOPHY.map((p, i) => (
              <div key={p.title} className="reveal text-center" style={{ transitionDelay: `${i * 0.12}s` }}>
                <span className="font-display text-6xl text-gold/40">0{i + 1}</span>
                <h3 className="font-serif text-3xl text-cream mt-4">{p.title}</h3>
                <div className="gold-line mx-auto my-6" />
                <p className="text-cream/70 font-light leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Venue */}
      <section className="py-24 md:py-32 bg-cream">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-16 items-center">
          <div className="reveal">
            <span className="text-xs tracking-luxe uppercase text-gold">Our Venue</span>
            <h2 className="font-display text-4xl md:text-5xl text-ink mt-4 leading-tight">Contemporary Space for Meaningful Gatherings</h2>
            <div className="gold-line my-8" />
            <p className="text-ink/70 font-light leading-relaxed mb-8">
              Vivamore offers a refined and contemporary venue experience thoughtfully designed for intimate celebrations, corporate gatherings, and private events in the heart of Kuala Lumpur. With flexible layouts and curated event arrangements, Vivamore transforms every gathering into a seamless and memorable experience.
            </p>
            <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-4">
              {VENUE_FEATURES.map((f) => (
                <li key={f} className="flex gap-3 text-sm text-ink/75 font-light">
                  <Check size={17} className="text-gold shrink-0 mt-0.5" /> {f}
                </li>
              ))}
            </ul>
          </div>
          <div className="reveal grid grid-cols-2 gap-4">
            <div className="img-hover-zoom overflow-hidden h-[500px]">
              <img src={IMAGES.banquet1} alt="Venue" className="w-full h-full object-cover" />
            </div>
            <div className="img-hover-zoom overflow-hidden h-[500px] mt-12">
              <img src={IMAGES.diningAmbiance} alt="Interior" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* Commitment */}
      <section className="py-24 md:py-32 bg-ink">
        <div className="max-w-5xl mx-auto px-6">
          <div className="reveal text-center flex flex-col items-center mb-14">
            <span className="text-xs tracking-luxe uppercase text-gold-light">Our Commitment</span>
            <h2 className="font-display text-4xl md:text-5xl text-cream mt-4">Excellence in Every Detail</h2>
            <div className="gold-line mt-6" />
          </div>
          <div className="grid sm:grid-cols-2 gap-6">
            {COMMITMENTS.map((c, i) => (
              <div key={c} className="reveal flex gap-4 border border-cream/15 p-6" style={{ transitionDelay: `${i * 0.1}s` }}>
                <Check size={20} className="text-gold-light shrink-0 mt-0.5" />
                <p className="text-cream/80 font-light">{c}</p>
              </div>
            ))}
          </div>
          <div className="reveal text-center mt-14">
            <Link to="/contact" className="btn-gold inline-flex items-center gap-2 text-xs tracking-widest uppercase px-8 py-4 border border-gold text-gold hover:bg-gold hover:text-ink">
              Get in Touch <ArrowRight size={15} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
